import API_BASE from '../../utils/api';
import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import Sidebar from '../../components/Sidebar';
// Add this import at the top
import ProfileDropdown from '../../components/ProfileDropdown';

// Replace the nav section with this:

const ParentTransport = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [transportDetails, setTransportDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    if (user?.studentId) {
      fetchTransportDetails();
    } else {
      setLoading(false);
    }
  }, [user]);

  const fetchTransportDetails = async () => {
    try {
      // First get student details to get transport ID
      const studentResponse = await axios.get(`${API_BASE}/students/child/${user.studentId}`);
      const student = studentResponse.data;
      
      if (student.transportId) {
        // Fetch transport route details
        const transportResponse = await axios.get(`${API_BASE}/transport/${student.transportId}`);
        setTransportDetails(transportResponse.data);
      } else {
        setError('No transport route assigned to your child');
      }
    } catch (error) {
      console.error('Error fetching transport details:', error);
      setError('Failed to load transport details');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  if (loading) {
    return (
      <div className="flex">
        <Sidebar />
        <div className="flex-1 bg-gray-100 flex items-center justify-center h-screen">
          <div className="text-xl">Loading...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex">
      <Sidebar />
      <div className="flex-1 bg-gray-100 min-h-screen">
        <nav className="bg-white shadow-sm sticky top-0 z-10">
          <div className="px-6 py-4 flex justify-between items-center">
            <h1 className="text-2xl font-semibold text-gray-800">Transport Details</h1>
            <ProfileDropdown />
          </div>
        </nav>
               

        <div className="p-6">
          {error ? (
            <div className="bg-white rounded-lg shadow p-12 text-center">
              <span className="text-6xl mb-4 block">🚌</span>
              <h2 className="text-xl font-semibold text-gray-800 mb-2">No Transport Assigned</h2>
              <p className="text-gray-500">{error}</p>
            </div>
          ) : transportDetails ? (
            <div className="space-y-6">
              {/* Route Information */}
              <div className="bg-white rounded-lg shadow overflow-hidden">
                <div className="px-6 py-4 bg-indigo-600 text-white">
                  <h2 className="text-lg font-semibold">Route Information</h2>
                </div>
                <div className="p-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <p className="text-sm text-gray-500">Route Name</p>
                      <p className="font-medium text-lg">{transportDetails.routeName}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Route Number</p>
                      <p className="font-medium text-lg">{transportDetails.routeNumber}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Vehicle Number</p>
                      <p className="font-medium">{transportDetails.vehicleNumber}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Vehicle Type</p>
                      <p className="font-medium capitalize">{transportDetails.vehicleType}</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Driver Information */}
              <div className="bg-white rounded-lg shadow overflow-hidden">
                <div className="px-6 py-4 bg-green-600 text-white">
                  <h2 className="text-lg font-semibold">Driver Information</h2>
                </div>
                <div className="p-6">
                  <div className="flex items-center space-x-4 mb-4">
                    <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center">
                      <span className="text-3xl">👨‍✈️</span>
                    </div>
                    <div>
                      <h3 className="font-semibold text-lg">{transportDetails.driverName}</h3>
                      <p className="text-gray-600">Driver</p>
                    </div>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <p className="text-sm text-gray-500">Phone Number</p>
                      <p className="font-medium">
                        <a href={`tel:${transportDetails.driverPhone}`} className="text-indigo-600 hover:underline">
                          {transportDetails.driverPhone}
                        </a>
                      </p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">License Number</p>
                      <p className="font-medium">{transportDetails.driverLicense}</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Route Stops */}
              <div className="bg-white rounded-lg shadow overflow-hidden">
                <div className="px-6 py-4 bg-yellow-600 text-white">
                  <h2 className="text-lg font-semibold">Route Stops</h2>
                </div>
                <div className="p-6">
                  {transportDetails.stops && transportDetails.stops.length > 0 ? (
                    <div className="space-y-4">
                      {transportDetails.stops
                        .sort((a, b) => a.stopOrder - b.stopOrder)
                        .map((stop, index) => (
                          <div key={index} className="flex items-start space-x-4">
                            <div className="flex-shrink-0 w-8 h-8 bg-indigo-100 rounded-full flex items-center justify-center">
                              <span className="font-semibold text-indigo-600">{stop.stopOrder}</span>
                            </div>
                            <div className="flex-1">
                              <div className="flex justify-between">
                                <h3 className="font-medium">{stop.stopName}</h3>
                                <span className="text-sm text-gray-500">{stop.stopTime}</span>
                              </div>
                              {stop.address && (
                                <p className="text-sm text-gray-500 mt-1">{stop.address}</p>
                              )}
                              {stop.landmark && (
                                <p className="text-xs text-gray-400 mt-1">Landmark: {stop.landmark}</p>
                              )}
                            </div>
                          </div>
                        ))}
                    </div>
                  ) : (
                    <p className="text-gray-500 text-center py-4">No stops information available</p>
                  )}
                </div>
              </div>

              {/* Additional Information */}
              <div className="bg-white rounded-lg shadow overflow-hidden">
                <div className="px-6 py-4 bg-purple-600 text-white">
                  <h2 className="text-lg font-semibold">Additional Information</h2>
                </div>
                <div className="p-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <p className="text-sm text-gray-500">Status</p>
                      <p className={`font-medium ${
                        transportDetails.status === 'active' ? 'text-green-600' : 
                        transportDetails.status === 'maintenance' ? 'text-yellow-600' : 'text-red-600'
                      }`}>
                        {transportDetails.status.charAt(0).toUpperCase() + transportDetails.status.slice(1)}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Capacity</p>
                      <p className="font-medium">{transportDetails.capacity} students</p>
                    </div>
                    {transportDetails.insuranceExpiry && (
                      <div>
                        <p className="text-sm text-gray-500">Insurance Expiry</p>
                        <p className="font-medium">{new Date(transportDetails.insuranceExpiry).toLocaleDateString()}</p>
                      </div>
                    )}
                    {transportDetails.fitnessExpiry && (
                      <div>
                        <p className="text-sm text-gray-500">Fitness Expiry</p>
                        <p className="font-medium">{new Date(transportDetails.fitnessExpiry).toLocaleDateString()}</p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
};

export default ParentTransport;